import dayjs from 'dayjs';
import { DARK, Flavor, LIGHT } from '@protomaps/basemaps';
import { Feature, FeatureCollection } from 'geojson';
import PocketBase, { SendOptions } from 'pocketbase';
import { MapGeoJSONFeature, MapRef } from 'react-map-gl/maplibre';
import { create } from 'zustand';
import { nprogress } from '@mantine/nprogress';
import { Bounds, HeatmapData, IntervalData, Sighting, StatsData } from './models';

const defaultSpecies = (window?.localStorage.getItem('id_species') || '329')
  .split(',')
  .map((s) => parseInt(s, 10));

export interface Store {
  pb: PocketBase;
  theme: Flavor;
  stats?: StatsData;
  id_species: number[];
  threshold: number;
  range?: [string | null, string | null];
  date: string | null;
  cluster: boolean;
  icons: boolean;
  bounds: Bounds;
  daysDiff: number;
  count: number;
  sightings: Sighting[];
  details: Sighting[];
  series: IntervalData[];
  heatmapData: HeatmapData;
  depts: Feature[];
  fetchData: (forcePlaceAllSpecies: boolean, withSeries: boolean) => void;
  fetchYearlySeries: () => void;
  calcDayDiff: () => void;
  calcRanges: (delta: number) => [string | null, string | null];
  initDates: (delta: number) => void;
  next: (factor: number) => void;
  loadStats: () => void;
  setIdSpecies: (selected_species: number[]) => void;
  setThreshold: (threshold: number) => void;
  setRange: (range: [string | null, string | null]) => void;
  setDate: (date: string | null) => void;
  toggleCluster: () => void;
  toggleIcons: () => void;
  loadDepts: () => void;
  setNewBounds: (map: MapRef) => void;
  setTheme: (theme: Flavor) => void;
  sightingsGeoJson?: FeatureCollection;
  setSightingsGeoJson: (features: FeatureCollection) => void;
  setDetails: (withSpecies: boolean) => void;
  feature?: MapGeoJSONFeature;
  setFeature: (feature: MapGeoJSONFeature | undefined) => void;
  resetDetails: () => void;
  forcePlaceDetails: boolean;
  setForcePlaceDetails: (forcePlaceDetails: boolean) => void;
  idSpeciesToStr: (numbers: number[]) => string;
  idSpeciesToPocketBaseIds: (numbers: number[]) => string;
}

export const useStore = create<Store>((set, get) => ({
  pb: new PocketBase(),
  theme:
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? DARK : LIGHT,
  id_species: defaultSpecies,
  threshold: 0,
  forcePlaceDetails: false,
  range: undefined,
  date: null,
  cluster: false,
  icons: false,
  daysDiff: 6,
  count: 0,
  bounds: {
    x0: -180.0,
    x1: 180.0,
    y0: 90.0,
    y1: -90.0,
  },
  series: [],
  heatmapData: {
    maxCount: 0,
    data: {},
  },
  sightings: [],
  details: [],
  depts: [],
  calcDayDiff: () => {
    const store = get();
    if (store.stats && store.range) {
      const max = dayjs(store.stats.last);
      const min = dayjs(store.stats.first);
      let diff =
        store.range[0] && store.range[1] ? dayjs(store.range[1]).diff(store.range[0], 'day') : -1;
      if (diff === max.diff(min, 'day')) {
        diff = -1;
      }
      set({ daysDiff: diff });
    }
  },
  calcRanges: (delta: number): [string | null, string | null] => {
    const store = get();
    if (!store.stats?.first || !store.stats?.last) {
      return [null, null];
    }
    const max = dayjs(store.stats.last);
    const min = dayjs(store.stats.first);
    let now = dayjs();
    if (now.diff(max) > 0) {
      now = max.clone();
    }
    let d = delta;
    if (d === -1) {
      d = max.diff(min, 'day');
    }
    return [now.subtract(d, 'day').toISOString(), now.toISOString()];
  },
  initDates: (delta: number) => {
    const store = get();
    if (!store.stats?.first || !store.stats?.last) {
      return;
    }
    const max = dayjs(store.stats.last);
    const min = dayjs(store.stats.first);
    let now = dayjs();
    if (now.diff(max) > 0) {
      now = max.clone();
    }
    let d = delta;
    if (d === -1) {
      d = max.diff(min, 'day');
    }
    set({ range: store.calcRanges(d), date: now.subtract(-1, 'day').toISOString() });
  },
  next: (factor: number) => {
    const store = get();
    if (store.range && store.range[0] && store.range[1]) {
      const start = dayjs(store.range[0]);
      const end = dayjs(store.range[1]);
      const diff = (start.diff(end, 'day') - 1) * factor;
      const range: [string | null, string | null] = [
        start.subtract(diff, 'day').toISOString(),
        end.subtract(diff, 'day').toISOString(),
      ];
      set({ range, date: range[0] });
    }
  },
  idSpeciesToStr: (numbers: number[]): string => {
    return numbers.map((s) => s.toString()).join(',');
  },

  idSpeciesToPocketBaseIds: (numbers: number[]): string => {
    const store = get();
    const pocketbase_ids = numbers.map(
      (s) => store.stats?.species.find((sp) => sp.id_species === s)?.id
    );
    return pocketbase_ids && pocketbase_ids.length > 0 ? pocketbase_ids.join(',') : '0';
  },
  fetchData: async (forcePlaceAllSpecies: boolean, withSeries: boolean) => {
    const store = get();
    let sightings: Sighting[] = [];
    const first =
      store.range && store.range[0] ? dayjs(store.range[0]) : dayjs(store.stats?.first as Date);
    const last =
      store.range && store.range[1] ? dayjs(store.range[1]) : dayjs(store.stats?.last as Date);
    if (first && last) {
      if (withSeries) {
        const selected_species = store.idSpeciesToPocketBaseIds(store.id_species);
        store.pb
          ?.send(
            `api/series?species=${selected_species}&from=${first.format(
              'YYYY-MM-DD'
            )}&to=${last.format('YYYY-MM-DD')}`,
            {
              method: 'GET',
            }
          )
          .then((series: IntervalData[]) => {
            const s = series.map(
              (s) =>
                ({
                  period: s.period.substring(0, 10),
                  label: s.label,
                  count: s.count,
                }) as IntervalData
            );
            const now = dayjs().format('MM-DD');
            for (let i = 0; i < s.length; i++) {
              if (
                s[i].period.substring(5) <= now &&
                (i === s.length - 1 || s[i + 1].period.substring(5) > now)
              ) {
                s[i].fill = '#FF0000';
              }
            }
            set({ series: s });
          });
      }

      let placeFilter =
        store.id_species.length === 0 && store.bounds
          ? `place.longitude >= ${store.bounds.x0} && place.longitude <= ${
              store.bounds.x1
            } && place.latitude >= ${store.bounds.y1} && place.latitude <= ${store.bounds.y0}`
          : '';
      let speciesFilter = (store.id_species || [])
        .map((id) =>
          store.pb.filter('species = {:id}', { id: store.stats?.speciesMapPerIdSpecies[id]?.id })
        )
        .join('||');
      if (forcePlaceAllSpecies && store.feature) {
        speciesFilter = '';
        placeFilter = `place = '${store.feature.properties.place}'`;
      }

      sightings = await store.pb.collection('observations').getFullList<Sighting>({
        filter: `moment >= '${first.toISOString().replace('T', ' ')}' && moment < '${last.add(1, 'day').toISOString().replace('T', ' ')}'${
          speciesFilter.length > 0 ? ` && (${speciesFilter})` : ''
        }${placeFilter.length > 0 ? ` && (${placeFilter})` : ''}`,
        skipTotal: true,
        sort: '-moment',
        expand: 'place',
        query: {
          threshold: !speciesFilter ? store.threshold : '0',
        },
      });
    }
    if (forcePlaceAllSpecies && store.feature) {
      set({ details: sightings });
    } else {
      set({ sightings, count: sightings.length });
    }
  },
  fetchYearlySeries: async () => {
    const store = get();
    if (store.stats) {
      const selected_species = store.idSpeciesToPocketBaseIds(store.id_species);
      store.pb
        ?.send(
          `api/series?species=${selected_species}&from=${dayjs(store.stats.first).format(
            'YYYY-01-01'
          )}&to=${dayjs(store.stats.last).format('YYYY-12-31')}&increment=1`,
          {
            method: 'GET',
          }
        )
        .then(async (series: IntervalData[]) => {
          const heatmapFullData: {
            [key: string]: number;
          } = series.reduce(
            (data, intervalData) => ({
              ...data,
              [intervalData.period.substring(0, 10)]: intervalData.count,
            }),
            {}
          );
          const heatmapData: HeatmapData = {
            maxCount: 0,
            data: {},
          };
          for (const [key, count] of Object.entries(heatmapFullData)) {
            const year = key.substring(0, 4);
            if (!heatmapData.data[year]) {
              heatmapData.data[year] = {};
            }
            heatmapData.data[year][key] = count;
            if (count > heatmapData.maxCount) {
              heatmapData.maxCount = count;
            }
          }
          set({ heatmapData });
        });
    }
  },
  loadStats: async () => {
    const store = get();
    if (store.pb) {
      let ongoing = 0;
      store.pb.beforeSend = (url: string, options: SendOptions) => {
        nprogress.start();
        ongoing++;
        return { url, options };
      };
      store.pb.afterSend = (_, data) => {
        ongoing--;
        if (ongoing <= 0) {
          nprogress.complete();
        }
        return data;
      };
    }
    const stats: StatsData = await store.pb?.send('api/stats', { method: 'GET' });
    stats.first = dayjs(stats.first).toDate();
    stats.last = dayjs(stats.last).toDate();
    stats.species = stats.species
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((s) => {
        s.value = s.id;
        s.label = s.name;
        s.search_label = `${s.name.normalize('NFD')} ${s.latin_name?.normalize('NFD')}`.replace(
          /\p{Diacritic}/gu,
          ''
        ); // https://stackoverflow.com/a/37511463
        return s;
      });
    stats.speciesMapPerId = {};
    stats.speciesMapPerId = stats.species.reduce(function (map, species) {
      map[species.id] = species;
      return map;
    }, stats.speciesMapPerId);
    stats.speciesMapPerIdSpecies = {};
    stats.speciesMapPerIdSpecies = stats.species.reduce(function (map, species) {
      map[species.id_species] = species;
      return map;
    }, stats.speciesMapPerIdSpecies);
    set({ stats });
  },
  setNewBounds: async (map: MapRef) => {
    const bounds = map.getBounds();
    const nw = bounds.getNorthWest();
    const se = bounds.getSouthEast();
    set({ bounds: { x0: nw.lng, x1: se.lng, y0: nw.lat, y1: se.lat } });
  },
  loadDepts: async () => {
    const store = get();
    const numeros: string[] = ['17', '29', '33', '36', '49', '56', '85', '44'];
    set({
      depts: (
        await Promise.all(
          numeros.map(async (num: string) => {
            return (await store.pb?.send(`/geojson/${num}.geojson`, {
              method: 'GET',
            })) as Feature;
          })
        )
      ).filter((a) => !!a),
    });
  },
  setIdSpecies: (id_species: number[]) => {
    set({ id_species, threshold: id_species.length > 0 ? 0 : get().threshold });
    localStorage.setItem('id_species', get().idSpeciesToStr(id_species));
  },
  setThreshold: (threshold: number) => {
    set({ threshold: threshold });
  },
  setRange: (range: [string | null, string | null]) => {
    set({ range });
  },
  setDate: (date: string | null) => {
    set({ date });
  },
  toggleCluster: () => {
    set({ cluster: !get().cluster });
  },
  toggleIcons: () => {
    set({ icons: !get().icons });
  },
  setTheme: (theme: Flavor) => {
    set({ theme });
  },
  setSightingsGeoJson: (sightingsGeoJson: FeatureCollection) => {
    set({ sightingsGeoJson });
  },
  resetDetails: async () => {
    set({ details: [], feature: undefined });
  },
  setDetails: async (withSpecies: boolean) => {
    const store = get();
    if (withSpecies) {
      const details = store.sightings.filter(
        (sighting: Sighting) =>
          sighting.expand?.place?.latitude === store.feature?.properties.latitude &&
          sighting.expand?.place?.longitude === store.feature?.properties.longitude
      );
      set({ details });
    } else {
      store.fetchData(true, false);
    }
  },
  setFeature: (feature: MapGeoJSONFeature | undefined) => {
    set({ feature });
  },
  setForcePlaceDetails: (forcePlaceDetails: boolean) => {
    set({ forcePlaceDetails });
    get().setDetails(!forcePlaceDetails);
  },
}));
